import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class TokenService {
    private readonly ACCESS_TOKEN_KEY = 'aap_access_token';
    private readonly REFRESH_TOKEN_KEY = 'aap_refresh_token';
    private readonly ID_TOKEN_KEY = 'aap_id_token';

    constructor() { }

    // Access Token
    getAccessToken(): string | null {
        return localStorage.getItem(this.ACCESS_TOKEN_KEY);
    }

    setAccessToken(token: string): void {
        localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
    }

    removeAccessToken(): void {
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    }

    // Refresh Token
    getRefreshToken(): string | null {
        return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }

    setRefreshToken(token: string): void {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
    }

    removeRefreshToken(): void {
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }

    // ID Token
    getIdToken(): string | null {
        return localStorage.getItem(this.ID_TOKEN_KEY);
    }

    setIdToken(token: string): void {
        localStorage.setItem(this.ID_TOKEN_KEY, token);
    }

    removeIdToken(): void {
        localStorage.removeItem(this.ID_TOKEN_KEY);
    }

    // Utilities
    clearAllTokens(): void {
      this.removeAccessToken();
      this.removeRefreshToken();
      this.removeIdToken();
    }

    clear(): void {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
            localStorage.removeItem(this.ID_TOKEN_KEY);
    }

    isTokenExpired(token: string | null = this.getAccessToken()): boolean {
        if (!token) {
            return true;
        }
        try {
            const payload = this.decodeToken(token);
            if (!payload || !payload.exp) {
                return true;
            }

            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp < currentTime;
        } catch (e) {
            return true;
        }
    }

    decodeToken(token: string | null = this.getAccessToken()): any {
        if (!token) return null;
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error('Error decoding token:', e);
            return null;
        }
    }

    hasValidAccessToken(): boolean {
        const token = this.getAccessToken();
        return !this.isTokenExpired(token);
    }

    hasRole(roles: string | string[]): boolean {
        const payload = this.decodeToken();
        if (!payload || !payload.roles) {
            return false;
        }

        const userRoles: string[] = Array.isArray(payload.roles) ? payload.roles : [payload.roles];
        const requiredRoles: string[] = Array.isArray(roles) ? roles : [roles];
        return requiredRoles.some(role => userRoles.includes(role));
    }

    hasPermission(permission: string | string[]): boolean {
        const payload = this.decodeToken();
        if (!payload || !payload.permissions) {
            return false;
        }

        const userPermissions: string[] = Array.isArray(payload.permissions) ? payload.permissions : [payload.permissions];
        const requiredPermissions = Array.isArray(permission) ? permission : [permission];

        return requiredPermissions.some(p => userPermissions.includes(p));
    }
}
